import React, { useMemo, useEffect, FC, useState } from 'react';
import classNames from 'classnames';
import { BrandProvider, GlobalAlertProvider } from './contextProviders';
import {
    useCustomTextInput,
    useDirectLineSession,
    useDisableTextSend,
    useInputMode,
    useLoadingTimeout,
    useConfig,
    DisableReasons,
} from './hooks';
import WebChat from './WebChat';
import { ConversationStatusProvider } from './contextProviders/ConversationStatusContext';
import {
    LiveChatEndConfirmation,
    closeChatBot,
    getGreetingConfigForTenant,
    getTenantName,
    isUnified,
    restartChatbot,
} from './helpers';
import { closeFeedbackData } from './feedbackOptions';
import {
    ApplicationModal,
    ApplicationModalPropsType,
} from './jsxElements/ApplicationModal';
import { NewFeedbackDialog } from './jsxElements/NewFeedbackDialog';
import { InitLoaderValues, TenantGreetingConfig } from './types';
import styles from './App.module.css';
import { StyledEngineProvider } from '@mui/material/styles';
import OfflineChat from './OfflineChat';
import { AmpFlagSet, amplitudeClients } from './amplitude';
import { SelectActionModal } from './jsxElements/SelectActionModal';
import { getLiveChatService } from './liveChatService';
import { GraceFeatureFlags } from './featureFlags';
import { removeAllExpiredItems } from './utils/localStorage';

import {
    setFeatureFlagAmplitudeUserProperties,
    setSessionAmplitudeUserProperties,
} from './analytics';
import { logEvent } from './logging';
import liveChatConfig from './resources/liveChatConfig.json';
import general from './resources/general.json';
import {
    handleMAChatbotOutage,
    postMessageAssistMessage,
} from './messageassist';
import { APIBasedReactChat } from './ReactChat';
import {
    getMessageRenderer,
    MessageRenderer,
} from './interfaces/messageRenderer';
import { MESSAGE_ASSIST_MESSAGE_EVENT_TYPES, TENANTS } from './constants';
import { Spinner } from './jsxElements/Spinner';
import isObjectEmpty from './utils/isObjectEmpty';
import GlobalInformationAlert from './GlobalInformationAlert';

const liveChatService = getLiveChatService();

const App: FC<{
    healthSystemToken: string | null;
    initLoaderValues: InitLoaderValues | null;
}> = ({ healthSystemToken, initLoaderValues }) => {
    const { tier } = useConfig();
    const { setCustomTextInput } = useCustomTextInput();
    const { inputMode, setInputMode } = useInputMode();
    const { setDisableTextSend } = useDisableTextSend();
    const [feedbackDialogIsOpen, setFeedbackDialogIsOpen] = useState(false);
    const [liveChatEndConfirmation, setLiveChatEndConfirmation] =
        useState<LiveChatEndConfirmation>(LiveChatEndConfirmation.hide);
    const [modalProperties, setModalProperties] =
        useState<ApplicationModalPropsType>({
            isOpen: false,
        });
    const [interactedWithBot, setInteractedWithBot] = useState(false);
    const [feedbackDialogVisited, setFeedbackDialogVisited] = useState(false);
    const [featureFlags, setFeatureFlags] = useState<AmpFlagSet>({});
    const [amplitudeFlags, setAmplitudeFlags] = useState<AmpFlagSet>({});
    const [tenantGreetingConfig, setTenantGreetingConfig] =
        useState<TenantGreetingConfig>();
    const [shouldInitDirectLine, setShouldInitDirectLine] = useState(false);
    const [greetingMessages, setGreetingMessages] = useState<any[]>([]);
    const messageRenderer: MessageRenderer = getMessageRenderer();

    if (getTenantName() == TENANTS.MESSAGE_ASSIST) {
        useEffect(() => {
            postMessageAssistMessage(
                MESSAGE_ASSIST_MESSAGE_EVENT_TYPES.ON_GRACE_LOADING_UPDATE,
                '',
                'App rendered'
            ).catch(() => {
                // Do nothing
            });
        }, []);
    }

    useEffect(() => {
        logEvent({
            name: 'ConversationContext',
            properties: { unified: isUnified() },
        });
    }, []);

    useEffect(() => {
        removeAllExpiredItems();
    }, []);

    useEffect(() => {
        const fetchFlags = async () => {
            amplitudeClients.fetchFlags().then((flags) => {
                setAmplitudeFlags(flags);

                if (getTenantName() == TENANTS.MESSAGE_ASSIST) {
                    postMessageAssistMessage(
                        MESSAGE_ASSIST_MESSAGE_EVENT_TYPES.ON_GRACE_LOADING_UPDATE,
                        '',
                        'Feature flags fetched'
                    ).catch(() => {
                        // Do nothing
                    });
                }
            });
        };
        fetchFlags();
    }, []);

    useEffect(() => {
        if (isObjectEmpty(amplitudeFlags)) {
            return;
        }
        if (amplitudeFlags[GraceFeatureFlags.Outage] === true) {
            handleMAChatbotOutage(getTenantName());
        }
        setFeatureFlags(amplitudeFlags);
        setFeatureFlagAmplitudeUserProperties(
            amplitudeFlags,
            !!healthSystemToken
        );
        logEvent({
            name: 'Feature flags initialized',
            properties: {},
        });
    }, [amplitudeFlags]);

    useEffect(() => {
        if (isObjectEmpty(featureFlags)) {
            return;
        }
        // Pull greeting config from resources for the current
        // tenant and check greeting config feature flag.
        const tenantGreetingConfig = getGreetingConfigForTenant(featureFlags);
        // If greeting config found, present greeting to user
        // and prevent setup of DirectLine connection.
        // Otherwise, setup DirectLine connection and continue conversation.
        setTenantGreetingConfig(tenantGreetingConfig);
        setShouldInitDirectLine(!tenantGreetingConfig);
    }, [featureFlags]);

    const [directLine, directLineIsLoading] = useDirectLineSession(
        shouldInitDirectLine,
        featureFlags,
        healthSystemToken
    );
    useLoadingTimeout();

    useEffect(() => {
        if (directLine) {
            setSessionAmplitudeUserProperties(directLine.session);
        }
    }, [directLine]);

    useEffect(() => {
        // Enable/Disable input based on DirectLine connection status
        setDisableTextSend(
            directLineIsLoading,
            DisableReasons.DIRECTLINE_LOADING
        );
    }, [directLineIsLoading]);

    // we don't EVER want to re-render a ReactWebChat, as it destroys the chat session.
    // not really a super-reacty component :)
    // see https://github.com/facebook/react/issues/15156#issuecomment-474590693 for more
    // deets on why we can useMemo for this

    const getChat = () => {
        if (amplitudeFlags) {
            if (amplitudeFlags[GraceFeatureFlags.Outage] === true) {
                return (
                    <OfflineChat
                        key={'webchat'}
                        featureFlags={featureFlags}
                        messageRenderer={messageRenderer}
                    />
                );
            }

            if (!shouldInitDirectLine) {
                // Present client greeting from config without Rasa conversation
                return (
                    <APIBasedReactChat
                        featureFlags={featureFlags}
                        healthSystemToken={healthSystemToken}
                        modalProperties={modalProperties}
                        tenantGreetingConfig={tenantGreetingConfig}
                        setModalProperties={setModalProperties}
                        setInputMode={setInputMode}
                        setGreetingMessages={setGreetingMessages}
                        setShouldInitDirectLine={setShouldInitDirectLine}
                        messageRenderer={messageRenderer}
                    />
                );
            }

            if (directLine) {
                // Present Rasa conversation component
                // with initial client greeting if present
                return (
                    <WebChat
                        key={'webchat'}
                        healthSystemToken={healthSystemToken}
                        initLoaderValues={initLoaderValues}
                        directLine={directLine}
                        featureFlags={featureFlags}
                        setCustomTextInput={setCustomTextInput}
                        setInputMode={setInputMode}
                        setDisableTextSend={setDisableTextSend}
                        modalProperties={modalProperties}
                        setModalProperties={setModalProperties}
                        interactedWithBot={interactedWithBot}
                        setFeedbackDialogIsOpen={setFeedbackDialogIsOpen}
                        setInteractedWithBot={setInteractedWithBot}
                        setLiveChatEndConfirmation={setLiveChatEndConfirmation}
                        skipGreeting={!!tenantGreetingConfig}
                        greetingMessages={greetingMessages}
                        messageRenderer={messageRenderer}
                    />
                );
            }
        }
        return null;
    };

    const chat = useMemo(() => {
        const feedbackElt = (
            <NewFeedbackDialog
                isOpen={feedbackDialogIsOpen}
                setIsOpen={setFeedbackDialogIsOpen}
                key={'closeFeedbackDialog'}
                feedbackEntity={closeFeedbackData}
                triggerSource="Close button"
                session={directLine?.session}
            />
        );

        const liveChatModalDialogCloseConfirmationActions = [
            {
                title: liveChatConfig.providence.endChatConfirm,
                onClick: () => {
                    liveChatService.userClose();
                    setLiveChatEndConfirmation(LiveChatEndConfirmation.hide);

                    if (
                        liveChatEndConfirmation ===
                        LiveChatEndConfirmation.showDisconnectAndClose
                    ) {
                        closeChatBot(
                            interactedWithBot,
                            setFeedbackDialogIsOpen
                        );
                    } else if (
                        liveChatEndConfirmation ===
                        LiveChatEndConfirmation.showDisconnectAndReset
                    ) {
                        restartChatbot(tier);
                    }
                },
            },
            {
                title: liveChatConfig.providence.endChatCancel,
                onClick: () =>
                    setLiveChatEndConfirmation(LiveChatEndConfirmation.hide),
            },
        ];

        return (
            <>
                <StyledEngineProvider injectFirst>
                    <ApplicationModal
                        modalProperties={modalProperties}
                        setModalProperties={setModalProperties}
                    />
                    {getChat()}
                    {feedbackElt}
                    <SelectActionModal
                        open={
                            liveChatEndConfirmation !==
                            LiveChatEndConfirmation.hide
                        }
                        title={liveChatConfig.providence.endChatTitle}
                        body={liveChatConfig.providence.endChatBody}
                        actions={liveChatModalDialogCloseConfirmationActions}
                    />
                </StyledEngineProvider>
            </>
        );
    }, [
        directLine,
        tenantGreetingConfig,
        healthSystemToken,
        setCustomTextInput,
        setInputMode,
        setDisableTextSend,
        feedbackDialogIsOpen,
        liveChatEndConfirmation,
        initLoaderValues,
        modalProperties,
        interactedWithBot,
        setInteractedWithBot,
    ]);

    useEffect(() => {
        if (feedbackDialogIsOpen) setFeedbackDialogVisited(true);

        if (feedbackDialogVisited && !feedbackDialogIsOpen) {
            const parentWindow = window.parent.window;
            if (parentWindow === window) {
                return;
            }
            parentWindow.postMessage('closeChatbot', '*');
        }
    }, [feedbackDialogIsOpen, feedbackDialogVisited]);

    if (directLine || tenantGreetingConfig) {
        if (getTenantName() == TENANTS.MESSAGE_ASSIST) {
            postMessageAssistMessage(
                MESSAGE_ASSIST_MESSAGE_EVENT_TYPES.ON_GRACE_LOAD_SUCCESS
            ).catch(() => {
                // Do nothing
            });
        }
        const chatStyles = classNames(
            styles.appRoot,
            `input_${inputMode}`,
            styles[getTenantName()],
            getTenantName()
        );
        return (
            <div className={chatStyles}>
                {/* Top-level provider keeps timers centralized and stable,
                        avoids re-mount resets, across greeting, OfflineChat, and WebChat */}
                <ConversationStatusProvider>
                    <GlobalAlertProvider>
                        <GlobalInformationAlert />
                        <BrandProvider>{chat}</BrandProvider>
                    </GlobalAlertProvider>
                </ConversationStatusProvider>
            </div>
        );
    }

    if (getTenantName() == TENANTS.MESSAGE_ASSIST) {
        return <Spinner />;
    }

    return (
        <div className={styles.loadingBotText}>
            {general.providence.botLoading}
        </div>
    );
};

export default App;
